import { DotsVerticalIcon, PencilIcon, TrashIcon } from "@heroicons/react/outline";
import { useMemo } from "react";

import { convertActionsToContextMenuItems } from "@/components/ui/Cards/Card/utils";
import { ContextMenu, ContextMenuItems } from "@/components/ui/ContextMenu";
import { Action, DropdownMenu, DropdownMenuVariant } from "@/components/ui/DropdownMenu";
import { Mcp } from "@/components/ui/icons";
import { McpServer } from "@/queries/mcp-servers";

interface McpServerCardProps {
  server: McpServer;
  onEdit: (server: McpServer) => void;
  onDelete: (server: McpServer) => void;
  isDeleting?: boolean;
}

const McpServerCard = ({ server, onEdit, onDelete, isDeleting = false }: McpServerCardProps) => {
  const menuItems: Action<McpServer>[] = useMemo(
    () => [
      {
        id: "edit",
        label: "Edit",
        icon: PencilIcon,
        variant: DropdownMenuVariant.PRIMARY,
        onClick: () => onEdit(server),
      },
      {
        id: "delete",
        label: "Delete",
        icon: TrashIcon,
        variant: DropdownMenuVariant.DESTRUCTIVE,
        getDisabled: () => isDeleting,
        onClick: () => onDelete(server),
      },
    ],
    [isDeleting, onDelete, onEdit, server],
  );

  const contextMenuItems = useMemo(
    () => convertActionsToContextMenuItems(menuItems) as ContextMenuItems<McpServer>[],
    [menuItems],
  );

  return (
    <ContextMenu contextMenuItems={contextMenuItems} data={server} modal={false} className="h-full">
      <div
        role="button"
        tabIndex={0}
        className="flex h-full w-full cursor-pointer flex-col rounded-lg border border-gray-200 bg-white px-4 py-4 text-left transition-colors hover:border-gray-300 hover:bg-gray-50"
        onClick={() => onEdit(server)}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onEdit(server);
          }
        }}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-700">
            <Mcp className="h-5 w-5" />
          </div>
          <h3 className="min-w-0 truncate text-base font-medium text-gray-900">{server.name}</h3>
          <div className="ml-auto shrink-0">
            <DropdownMenu
              menuItems={menuItems}
              item={server}
              icon={<DotsVerticalIcon className="h-5 w-5" />}
              triggerClassName="rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            />
          </div>
        </div>
        <p className="mt-3 line-clamp-2 min-h-10 text-sm text-gray-600">
          {server.description || "Remote Model Context Protocol server"}
        </p>
        <div className="mt-3 min-w-0">
          <span className="block truncate rounded bg-gray-100 px-2 py-1 text-xs text-gray-500">
            {server.config.server_url}
          </span>
        </div>
      </div>
    </ContextMenu>
  );
};

export default McpServerCard;
